import React, { useEffect, useState, useRef } from 'react';
import { Card, Form, Select, Checkbox, Button, message, Row, Col, Table, Breadcrumb, Space } from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import debounce from 'lodash.debounce';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useSelector } from 'react-redux';

import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const { Option } = Select;
const breadcrumbItems = [
    { title: 'Setting' },
    { title: 'Permission' },
];
interface ModuleRight {
    module_id: number;
    module_url: string;
    module_name: string;
    module_parent: number;
    right_view: 'Y' | 'N';
    right_add: 'Y' | 'N';
    right_edit: 'Y' | 'N';
    right_delete: 'Y' | 'N';
    right_import?: 'Y' | 'N';
    right_export?: 'Y' | 'N';
    right_search?: 'Y' | 'N';
}

interface User {
    user_id: number;
    user_name: string;
}

const actionMap: Record<string, keyof ModuleRight> = {
    view: 'right_view',
    create: 'right_add',
    edit: 'right_edit',
    delete: 'right_delete',
    import: 'right_import',
    export: 'right_export',
    search: 'right_search',
};

const allActions = Object.keys(actionMap);

interface TableRow {
    key: number;
    module_name: string;
    module_url: string;
    module_parent: number;
    isParent: boolean;
    children?: TableRow[];
}
interface Organization {
    organization_id: number;
    organization_name: string;
}
const Permission = () => {
    const user = useSelector((state: any) => state.user?.user);
    const [form] = Form.useForm();
    const [selectedUser, setSelectedUser] = useState<number | null>(null);
    const [permissions, setPermissions] = useState<Record<number, string[]>>({});
    const [users, setUsers] = useState<User[]>([]);
    const [modules, setModules] = useState<ModuleRight[]>([]);

    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const parentModules = modules.filter((mod) => mod.module_parent === 0);
    const childModules = modules.filter((mod) => mod.module_parent !== 0);
    const currentModule = useCurrentModule();
    const getChildren = (parentId: number) =>
        childModules.filter((child) => child.module_parent === parentId);
    const fetchUsers = async (organizationId: number) => {
        try {
            const data = await apiRequest<User[]>({
                method: 'get',
                url: `/users?organization_id=${organizationId}`
            });
            setUsers(data);
        } catch {
            showError('Failed to load users');
        }
    };

    useEffect(() => {
        const loadUserModulesAndPermissions = async () => {
            if (!selectedUser) return;

            try {
                const res = await apiRequest({
                    method: 'get',
                    url: `modules?user_id=${selectedUser}`,
                });

                const fetchedModules = Array.isArray(res.data) ? res.data : res;

                const uniqueModules = Array.from(
                    new Set(fetchedModules.map((mod: ModuleRight) => mod.module_id))
                ).map((id) => fetchedModules.find((mod: ModuleRight) => mod.module_id === id));

                setModules(uniqueModules as ModuleRight[]);

                const permissionMap: Record<number, string[]> = {};
                uniqueModules.forEach((mod: ModuleRight) => {
                    const actions: string[] = [];
                    Object.entries(actionMap).forEach(([actionKey, rightKey]) => {
                        if (mod[rightKey] === 'Y') actions.push(actionKey);
                    });
                    permissionMap[mod.module_id] = actions;
                });

                setPermissions(permissionMap);
            } catch {
                showError('Failed to load user permissions');
            }
        };

        loadUserModulesAndPermissions();
    }, [selectedUser]);
    const debouncedUpdate = useRef(
        debounce(async (payload) => {
            try {
                await apiRequest({
                    method: 'put',
                    url: 'update-user-permission',
                    data: { permissions: [payload] },
                });
                showSuccess('Permission updated');
            } catch {
                showError('Failed to update permission');
            }
        }, 500)
    ).current;
    const handlePermissionChange = (moduleId: number, updatedValues: string[], clicked: string) => {
        const actionsRequiringView = ['edit', 'delete', 'create', 'import', 'export', 'search'];

        let newPermissions = [...updatedValues];

        if (clicked === 'view' && !updatedValues.includes('view')) {
            newPermissions = [];
        } else if (actionsRequiringView.includes(clicked) && !updatedValues.includes('view')) {
            newPermissions.push('view');
        }

        setPermissions((prev) => ({
            ...prev,
            [moduleId]: newPermissions,
        }));

        // Prepare payload for API
        const payload: any = {
            user_id: selectedUser,
            module_id: moduleId,
        };

        Object.entries(actionMap).forEach(([action, rightKey]) => {
            payload[rightKey] = newPermissions.includes(action) ? 'Y' : 'N';
        });

        // Call the debounced API function
        debouncedUpdate(payload);
    };

    const tableData: TableRow[] = parentModules
        .map((parent) => {
            const children = getChildren(parent.module_id);

            const hasChildren = children.length > 0;

            const parentRow: any = {
                key: parent.module_id,
                module_name: parent.module_name,
                module_url: parent.module_url,
                module_parent: parent.module_parent,
                isParent: hasChildren,
            };

            if (hasChildren) {
                parentRow.key = parent.module_id;
                parentRow.children = children.map((child) => ({
                    key: child.module_id,
                    module_name: child.module_name,
                    module_url: child.module_url,
                    module_parent: child.module_parent,
                    isParent: false,
                }));
            }

            return parentRow;
        });


    useEffect(() => {
        return () => {
            debouncedUpdate.cancel();
        };
    }, []);
    const columns = [
        {
            title: 'Module Name',
            dataIndex: 'module_name',
            key: 'module_name',
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            render: (text: string, record: any) => {
                const selected = permissions[record.key] || [];

                const handleCheck = (action: string, checked: boolean) => {
                    let newValues = checked
                        ? [...selected, action]
                        : selected.filter((val) => val !== action);

                    handlePermissionChange(record.key, newValues, action);
                };

                return (
                    <>
                        {allActions.map((action) => (
                            <Checkbox
                                key={action}
                                checked={selected.includes(action)}
                                onChange={(e) => handleCheck(action, e.target.checked)}
                            >
                                {action}
                            </Checkbox>
                        ))}
                    </>
                );
            },
        },
    ];
    useEffect(() => {
        apiRequest<Organization[]>({ method: 'get', url: '/organization' })
            .then(setOrganizations)
            .catch(() => showError('Failed to load organizations'));
    }, []);

    useEffect(() => {
        if (user?.organization_id && user?.user_role != 'super-master') {
            fetchUsers(user.organization_id);
            console.log("user", user);
        }
    }, [user.organization_id]);
    return (
        <>
            <Space
                style={{
                    display: 'flex',
                    marginBottom: '16px',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <Breadcrumb items={breadcrumbItems} />
            </Space>
            <Card title="User-Based Permission Management" style={{ margin: '0 auto' }}>
                <Form
                    form={form}
                    layout="inline"
                    style={{
                        marginTop: 20,
                        flexWrap: 'wrap',
                        display: 'block',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: 16,
                    }}
                >
                    <div
                        style={{
                            marginTop: 20,
                            flexWrap: 'wrap',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: 16,
                        }}>
                        {user?.user_role == 'super-master' && (
                            <Form.Item
                                name="organization_id"
                                rules={[{ required: true, message: 'Please select organization' }]}
                                style={{ width: 250 }}
                            >
                                <Select
                                    placeholder="Select Organization"
                                    options={organizations.map(org => ({ label: org.organization_name, value: org.organization_id }))}
                                    onChange={(value) => {
                                        fetchUsers(value); // pass the selected org ID
                                    }}
                                    style={{ width: 250 }}
                                />
                            </Form.Item>)}
                        <Form.Item required style={{ width: 250 }}>
                            <Select
                                placeholder="Choose a user"
                                onChange={setSelectedUser}
                                allowClear
                                showSearch
                                optionFilterProp="children"
                            >
                                {users.length === 0 ? (
                                    <Option value="no-users" disabled>
                                        No users available
                                    </Option>
                                ) : (
                                    users.map((user) => (
                                        <Option key={user.user_id} value={user.user_id}>
                                            {user.user_name}
                                        </Option>
                                    ))
                                )}
                            </Select>
                        </Form.Item>
                    </div>
                    {selectedUser && modules.length > 0 && (
                        <div style={{ marginTop: 24 }}>
                            <Table
                                dataSource={tableData}
                                columns={columns}
                                rowClassName="editable-row"
                                pagination={false}
                                scroll={{ x: 'max-content' }}
                            />
                        </div>
                    )}

                    {/* <Row justify="end">
                        <Col>
                            <Button type="primary" onClick={handleSave}>
                                Save Permissions
                            </Button>
                        </Col>
                    </Row> */}
                </Form>
            </Card>
        </>
    );
};

export default Permission;
